import React, { useEffect } from 'react';
import { Box, Flex, Text, TextField, Heading, RadioGroup, Separator } from '@radix-ui/themes';
import SectionHeading from './common/SectionHeading';

export interface VitalSigns {
  blood_pressure_systolic: string;
  blood_pressure_diastolic: string;
  height_feet: string;
  height_inches: string;
  weight_pounds: string;
  BMI: string;
  input_mode: 'height_weight' | 'bmi_only';
}

interface VitalSignsInputProps {
  vitalSigns: VitalSigns;
  onChange: (vitalSigns: VitalSigns) => void;
}

// Helper functions for conversions and calculations
const feetInchesToCentimeters = (feet: number, inches: number): number => {
  const totalInches = feet * 12 + inches;
  return Math.round(totalInches * 2.54);
};

const poundsToKilograms = (pounds: number): number => {
  return Math.round(pounds * 0.453592 * 10) / 10; // Round to 1 decimal place
};

const calculateBMI = (heightCm: number, weightKg: number): number => {
  const heightM = heightCm / 100;
  const bmi = weightKg / (heightM * heightM);
  return Math.round(bmi * 10) / 10; // Round to 1 decimal place
};

// Validation helpers
export const isValidSystolic = (value: string): boolean => {
  if (!value) return true; // Empty is valid (optional)
  const num = parseInt(value);
  return !isNaN(num) && num >= 60 && num <= 300;
};

export const isValidDiastolic = (value: string): boolean => {
  if (!value) return true; // Empty is valid (optional)
  const num = parseInt(value);
  return !isNaN(num) && num >= 40 && num <= 200;
};

const isValidFeet = (value: string): boolean => {
  if (!value) return true; // Empty is valid (optional)
  const num = parseInt(value);
  return !isNaN(num) && num >= 3 && num <= 8;
};

const isValidInches = (value: string, feet: string): boolean => {
  if (!value) return true; // Empty is valid (optional)
  const num = parseFloat(value);
  const feetNum = parseInt(feet);
  if (isNaN(num) || num < 0 || num >= 12) return false;
  
  // Check total height constraints
  if (!isNaN(feetNum)) {
    const totalInches = feetNum * 12 + num;
    // 100 cm = 39.37 inches (3'3"), 250 cm = 98.43 inches (8'2")
    return totalInches >= 39.37 && totalInches <= 98.43;
  }
  return true;
};

const isValidWeight = (value: string): boolean => {
  if (!value) return true; // Empty is valid (optional)
  const num = parseFloat(value);
  return !isNaN(num) && num >= 88 && num <= 1102;
};

const isValidBMI = (value: string): boolean => {
  if (!value) return true; // Empty is valid (optional)
  const num = parseFloat(value);
  return !isNaN(num) && num >= 10 && num <= 70;
};

const VitalSignsInput: React.FC<VitalSignsInputProps> = ({ vitalSigns, onChange }) => {
  // Auto-calculate BMI when height and weight are provided in height_weight mode
  useEffect(() => {
    if (vitalSigns.input_mode === 'height_weight') {
      const feet = parseInt(vitalSigns.height_feet);
      const inches = parseFloat(vitalSigns.height_inches);
      const weight = parseFloat(vitalSigns.weight_pounds);
      
      if (!isNaN(feet) && !isNaN(inches) && !isNaN(weight)) {
        const heightCm = feetInchesToCentimeters(feet, inches);
        const weightKg = poundsToKilograms(weight);
        const bmi = calculateBMI(heightCm, weightKg);
        
        if (vitalSigns.BMI !== bmi.toString()) {
          onChange({ ...vitalSigns, BMI: bmi.toString() });
        }
      } else if (vitalSigns.BMI) {
        // Clear BMI if height or weight is incomplete
        onChange({ ...vitalSigns, BMI: '' });
      }
    }
  }, [vitalSigns.height_feet, vitalSigns.height_inches, vitalSigns.weight_pounds, vitalSigns.input_mode]);

  const handleInputModeChange = (mode: 'height_weight' | 'bmi_only') => {
    onChange({
      ...vitalSigns,
      input_mode: mode,
      // Clear the non-selected fields
      ...(mode === 'bmi_only' ? {
        height_feet: '',
        height_inches: '',
        weight_pounds: ''
      } : {
        BMI: ''
      })
    });
  };

  const handleFieldChange = (field: keyof VitalSigns, value: string) => {
    onChange({ ...vitalSigns, [field]: value });
  };

  return (
    <Box className="mb-6">
      <SectionHeading noTopMargin>
        Vital Signs
      </SectionHeading>
      
      {/* Blood Pressure */}
      <Box className="mb-4">
        <Text size="2" weight="medium" className="mb-2 block">
          Blood Pressure (mmHg)
        </Text>
        <Flex gap="3" align="center">
          <Box className="flex-1">
            <TextField.Root size="3" className="w-full">
              <TextField.Input
                type="number"
                value={vitalSigns.blood_pressure_systolic}
                onChange={(e) => handleFieldChange('blood_pressure_systolic', e.target.value)}
                placeholder="Systolic (60-300)"
                min="60"
                max="300"
                style={!isValidSystolic(vitalSigns.blood_pressure_systolic) ? { borderColor: 'red', borderWidth: '2px' } : {}}
              />
            </TextField.Root>
            <Text size="1" color="gray" className="mt-1">Systolic</Text>
          </Box>
          <Text size="3" weight="bold" className="text-slate-400">/</Text>
          <Box className="flex-1">
            <TextField.Root size="3" className="w-full">
              <TextField.Input
                type="number"
                value={vitalSigns.blood_pressure_diastolic}
                onChange={(e) => handleFieldChange('blood_pressure_diastolic', e.target.value)}
                placeholder="Diastolic (40-200)"
                min="40"
                max="200"
                style={!isValidDiastolic(vitalSigns.blood_pressure_diastolic) ? { borderColor: 'red', borderWidth: '2px' } : {}}
              />
            </TextField.Root>
            <Text size="1" color="gray" className="mt-1">Diastolic</Text>
          </Box>
        </Flex>
        {(!isValidSystolic(vitalSigns.blood_pressure_systolic) || !isValidDiastolic(vitalSigns.blood_pressure_diastolic)) && (
          <Text size="1" color="red" className="mt-1">
            Systolic must be 60-300 mmHg, Diastolic must be 40-200 mmHg
          </Text>
        )}
      </Box>

      <Separator className="my-4" />

      {/* Input Mode Selection */}
      <Box className="mb-4">
        <Text size="2" weight="medium" className="mb-3 block">
          Choose Input Method
        </Text>
        <RadioGroup.Root
          value={vitalSigns.input_mode}
          onValueChange={(value) => handleInputModeChange(value as 'height_weight' | 'bmi_only')}
        >
          <Flex gap="4" direction="column">
            <Text as="label" size="2" className="flex items-center gap-2 cursor-pointer">
              <RadioGroup.Item value="height_weight" />
              <span>Enter Height & Weight (BMI will be calculated automatically)</span>
            </Text>
            <Text as="label" size="2" className="flex items-center gap-2 cursor-pointer">
              <RadioGroup.Item value="bmi_only" />
              <span>Enter BMI only</span>
            </Text>
          </Flex>
        </RadioGroup.Root>
      </Box>

      {/* Height and Weight Section */}
      <Box 
        className="mb-4 p-4 rounded-lg transition-all"
        style={{ 
          backgroundColor: vitalSigns.input_mode === 'height_weight' ? '#f8fafc' : '#f1f5f9',
          opacity: vitalSigns.input_mode === 'height_weight' ? 1 : 0.5,
          pointerEvents: vitalSigns.input_mode === 'height_weight' ? 'auto' : 'none'
        }}
      >
        <Text size="2" weight="medium" className="mb-2 block">
          Height & Weight
        </Text>
        
        {/* Height */}
        <Box className="mb-3">
          <Text size="2" className="mb-2 block text-slate-600">
            Height
          </Text>
          <Flex gap="3" align="center">
            <Box className="flex-1">
              <TextField.Root size="3" className="w-full">
                <TextField.Input
                  type="number"
                  value={vitalSigns.height_feet}
                  onChange={(e) => handleFieldChange('height_feet', e.target.value)}
                  placeholder="Feet (3-8)"
                  min="3"
                  max="8"
                  disabled={vitalSigns.input_mode !== 'height_weight'}
                  style={!isValidFeet(vitalSigns.height_feet) ? { borderColor: 'red', borderWidth: '2px' } : {}}
                />
              </TextField.Root>
              <Text size="1" color="gray" className="mt-1">Feet</Text>
            </Box>
            <Box className="flex-1">
              <TextField.Root size="3" className="w-full">
                <TextField.Input
                  type="number"
                  value={vitalSigns.height_inches}
                  onChange={(e) => handleFieldChange('height_inches', e.target.value)}
                  placeholder="Inches (0-11)"
                  min="0"
                  max="11"
                  step="0.1"
                  disabled={vitalSigns.input_mode !== 'height_weight'}
                  style={!isValidInches(vitalSigns.height_inches, vitalSigns.height_feet) ? { borderColor: 'red', borderWidth: '2px' } : {}}
                />
              </TextField.Root>
              <Text size="1" color="gray" className="mt-1">Inches</Text>
            </Box>
          </Flex>
          {(!isValidFeet(vitalSigns.height_feet) || !isValidInches(vitalSigns.height_inches, vitalSigns.height_feet)) && (
            <Text size="1" color="red" className="mt-1">
              Height must be between 3 ft 3 in and 8 ft 2 in (100-250 cm)
            </Text>
          )}
        </Box>

        {/* Weight */}
        <Box>
          <Text size="2" className="mb-2 block text-slate-600">
            Weight (pounds)
          </Text>
          <TextField.Root size="3" className="w-full max-w-xs">
            <TextField.Input
              type="number"
              value={vitalSigns.weight_pounds}
              onChange={(e) => handleFieldChange('weight_pounds', e.target.value)}
              placeholder="Weight (88-1102 lb)"
              min="88"
              max="1102"
              step="0.1"
              disabled={vitalSigns.input_mode !== 'height_weight'}
              style={!isValidWeight(vitalSigns.weight_pounds) ? { borderColor: 'red', borderWidth: '2px' } : {}}
            />
          </TextField.Root>
          {!isValidWeight(vitalSigns.weight_pounds) && (
            <Text size="1" color="red" className="mt-1">
              Weight must be between 88 and 1,102 pounds (40-500 kg)
            </Text>
          )}
        </Box>
      </Box>

      {/* BMI Section */}
      <Box 
        className="mb-4 p-4 rounded-lg transition-all"
        style={{ 
          backgroundColor: vitalSigns.input_mode === 'bmi_only' ? '#f8fafc' : '#f1f5f9',
          opacity: vitalSigns.input_mode === 'bmi_only' ? 1 : (vitalSigns.input_mode === 'height_weight' && vitalSigns.BMI ? 1 : 0.5),
          pointerEvents: vitalSigns.input_mode === 'bmi_only' ? 'auto' : 'none'
        }}
      >
        <Text size="2" weight="medium" className="mb-2 block">
          BMI (Body Mass Index)
        </Text>
        <TextField.Root size="3" className="w-full max-w-xs">
          <TextField.Input
            type="number"
            value={vitalSigns.BMI}
            onChange={(e) => handleFieldChange('BMI', e.target.value)}
            placeholder="BMI (10-70)"
            min="10"
            max="70"
            step="0.1"
            disabled={vitalSigns.input_mode !== 'bmi_only'}
            readOnly={vitalSigns.input_mode === 'height_weight'}
            style={!isValidBMI(vitalSigns.BMI) ? { borderColor: 'red', borderWidth: '2px' } : {}}
          />
        </TextField.Root>
        {!isValidBMI(vitalSigns.BMI) && (
          <Text size="1" color="red" className="mt-1">
            BMI must be between 10 and 70 kg/m²
          </Text>
        )}
        {vitalSigns.input_mode === 'height_weight' && vitalSigns.BMI && (
          <Text size="1" color="green" className="mt-1">
            ✓ Calculated automatically from height and weight
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default VitalSignsInput;
export { feetInchesToCentimeters, poundsToKilograms, calculateBMI };

export const centimetersToFeetInches = (cm: number): { feet: number; inches: number } => {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet, inches };
};

export const kilogramsToPounds = (kg: number): number => {
  return Math.round(kg / 0.453592);
};
